// store.js

import { Capacitor } from '@capacitor/core';
import { createStore } from 'vuex';

import { Sentry } from '@/vendors/Sentry';
import { Posthog } from '@/vendors/Posthog';

import { Timestamp, FirebaseAuthentication, signOut, auth } from '@/firebaseConfig.js';
import { removeListeners } from '@/firebaseListeners.js';
import { ApiRequest, SendSuccess } from '@/helpers.js';

import { getters } from './getters';
import { recordings } from './recordings';
import { stats } from './stats';
import fcm from './fcm';
import { chats } from './chats';
import { playlists } from './playlists';
import { player } from './player';
import { VoiceRecorderWrapper } from '@/utils/VoiceRecorderWrapper';

const store = createStore({
  modules: {
    recs: recordings,
    chats,
    stats,
    fcm,
    playlists,
    player,
  },
  state: getInitialState(),
  actions: {
    // Auth
    setQuery({ commit }, data) {
      commit('setQuery', data.query);
    },
    clearAllData({ commit }) {
      commit('resetState');
      commit('recs/resetState');
      commit('stats/resetState');
      commit('playlists/resetState');
      commit('player/resetState');
    },
    async check() {
      const response = await ApiRequest({
        method: 'get',
        url: `${import.meta.env.VITE_API_URL}/check`,
        params: {
          version: import.meta.env.VITE_VERSION,
          platform: Capacitor.getPlatform(),
        },
      });
      return response.data;
    },
    async logout({ dispatch }) {
      await removeListeners();
      if (Capacitor.isNativePlatform()) {
        await dispatch('fcm/unregisterPush');
        await FirebaseAuthentication.signOut();
      }
      await signOut(auth);
      dispatch('clearAllData');
      Sentry.setUser(null);
      Posthog.reset();
      SendSuccess('Logged Out');
    },
    async updateUserProfile(_, data) {
      data.updatedAt = Timestamp.now();
      const response = await ApiRequest({
        method: 'put',
        url: `${import.meta.env.VITE_API_URL}/user`,
        data,
      });
      return response.data;
    },
    async uploadUserImage({ commit }, data) {
      let formData = new FormData();
      formData.append('file', data.file);
      const response = await ApiRequest({
        method: 'post',
        url: `${import.meta.env.VITE_API_URL}/user/image`,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          commit('setProgress', Math.floor((progressEvent.loaded * 100) / progressEvent.total));
        },
        data: formData,
      });
      commit('setProgress', 0);
      return response.data;
    },
    async fetchUserProfile({ state }, data) {
      if (state.userProfile?.uid === data.userId) {
        return state.userProfile;
      }

      const response = await ApiRequest({
        method: 'get',
        url: `${import.meta.env.VITE_API_URL}/user`,
        params: {
          userId: data.userId,
        },
      });
      return response.data.user;
    },
    // Organizations
    async getOrganizations({ commit, state }) {
      const fetch = async () => {
        const response = await ApiRequest({
          method: 'get',
          url: `${import.meta.env.VITE_API_URL}/organizations/user`,
        });
        commit('setOrganizations', response.data.organizations);
        const foundOrganizationIndex = response.data.organizations.findIndex(
          ({ id }) => id === state.currentOrganizationId
        );
        commit(
          'setCurrentOrganizationId',
          response.data.organizations[foundOrganizationIndex === -1 ? 0 : foundOrganizationIndex].id
        );

        return response.data.organizations;
      };
      const organizationsPromise = fetch();
      commit('setOrganizationsPromise', organizationsPromise);
      return organizationsPromise;
    },
    async deleteOrganizationMember({ dispatch }, data) {
      const response = await ApiRequest({
        method: 'delete',
        url: `${import.meta.env.VITE_API_URL}/organizations/user`,
        data,
      });
      await dispatch('getOrganizations');
      return response.data;
    },
    async deleteOrganizationInvite({ dispatch }, data) {
      const response = await ApiRequest({
        method: 'delete',
        url: `${import.meta.env.VITE_API_URL}/organizations/user/invite/${data.inviteId}`,
      });
      await dispatch('getOrganizations');
      return response.data;
    },
    async createOrganization(_, data) {
      const response = await ApiRequest({
        method: 'post',
        url: `${import.meta.env.VITE_API_URL}/organizations/user`,
        data,
      });
      return response.data;
    },
    async updateOrganization({ dispatch }, data) {
      const response = await ApiRequest({
        method: 'put',
        url: `${import.meta.env.VITE_API_URL}/organizations/user`,
        data,
      });
      await dispatch('getOrganizations');
      return response.data;
    },
    async uploadOrganizationImage({ commit }, data) {
      let formData = new FormData();
      formData.append('file', data.file);
      const response = await ApiRequest({
        method: 'post',
        url: `${import.meta.env.VITE_API_URL}/organizations/upload`,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          commit('setProgress', Math.floor((progressEvent.loaded * 100) / progressEvent.total));
        },
        data: formData,
        params: {
          organizationId: data.organizationId,
        },
      });
      commit('setProgress', 0);
      return response.data;
    },
    // Invites
    async sendOrganizationInvites({ dispatch }, data) {
      const response = await ApiRequest({
        method: 'post',
        url: `${import.meta.env.VITE_API_URL}/organizations/user/invite`,
        data,
      });
      await dispatch('getOrganizations');
      return response.data;
    },
    async getOrganizationInvite(_, data) {
      const response = await ApiRequest({
        method: 'get',
        url: `${import.meta.env.VITE_API_URL}/organizations/user/invite`,
        params: {
          inviteId: data.inviteId,
        },
      });
      return response.data;
    },
    async acceptOrganizationInvite(_, data) {
      const response = await ApiRequest({
        method: 'put',
        url: `/organizations/user/invite/${data.inviteId}`,
        data: {
          email: data.email,
          name: data.name,
          password: data.password,
        },
      });
      return response.data;
    },
    // Feedback
    async sendFeedback({}, data) {
      const response = await ApiRequest({
        method: 'post',
        url: `${import.meta.env.VITE_API_URL}/feedbacks`,
        data,
      });
      return response.data;
    },
    // Topics
    async addTopic({ getters, commit }, { topicName, keywords }) {
      const organization = getters.currentOrganization;
      if (!organization) {
        throw new Error('Network Error');
      }
      const response = await ApiRequest({
        method: 'post',
        url: `${import.meta.env.VITE_API_URL}/organizations/topics`,
        params: {
          organizationId: organization.id,
        },
        data: {
          topicName,
          keywords,
        },
      });
      const topics = response.data.topics;
      commit('replaceOrganization', { ...organization, topics });
      return organization;
    },
    async deleteTopic({ getters, commit }, { topicId }) {
      try {
        const organization = getters.currentOrganization;
        const response = await ApiRequest({
          method: 'delete',
          url: `${import.meta.env.VITE_API_URL}/organizations/topics/${topicId}`,
          params: {
            organizationId: organization?.id,
          },
        });
        const topics = response.data.topics;
        commit('replaceOrganization', { ...organization, topics });
        return organization;
      } catch (err) {
        console.error(err);
      }
    },
    async editTopic({ getters, commit }, { topicId, topicName, keywords }) {
      const organization = getters.currentOrganization;
      const response = await ApiRequest({
        method: 'put',
        url: `${import.meta.env.VITE_API_URL}/organizations/topics/${topicId}`,
        params: {
          organizationId: organization?.id,
        },
        data: {
          topicName,
          keywords,
        },
      });
      const topics = response.data.topics;
      commit('replaceOrganization', { ...organization, topics });
      return organization;
    },
    async addAiTemplate({ getters, commit }, { name, questions }) {
      const data = { name };
      if (questions) {
        data.questions = questions;
      }
      const organization = getters.currentOrganization;
      if (!organization) {
        throw new Error('Network Error');
      }
      const response = await ApiRequest({
        method: 'post',
        url: `${import.meta.env.VITE_API_URL}/organizations/${organization.id}/ai-templates`,
        data,
      });
      const newAiTemplate = response.data.new;
      commit('replaceOrganization', {
        ...organization,
        aiTemplates: organization.aiTemplates.concat([newAiTemplate]),
      });
      return newAiTemplate;
    },
    async deleteAiTemplate({ getters, commit }, { id }) {
      const organization = getters.currentOrganization;
      const response = await ApiRequest({
        method: 'delete',
        url: `${import.meta.env.VITE_API_URL}/organizations/${organization?.id}/ai-templates/${id}`,
      });
      const aiTemplates = response.data.aiTemplates;
      commit('replaceOrganization', { ...organization, aiTemplates });
      return organization;
    },
    async editAiTemplate({ getters, commit }, { id, name, questions }) {
      const organization = getters.currentOrganization;
      const response = await ApiRequest({
        method: 'put',
        url: `${import.meta.env.VITE_API_URL}/organizations/${organization?.id}/ai-templates/${id}`,
        data: {
          name,
          questions,
        },
      });
      const aiTemplates = response.data.aiTemplates;
      commit('replaceOrganization', {
        ...organization,
        aiTemplates,
      });
      return organization;
    },
    async readNotifications({ commit }, { notifications }) {
      for (const notificationId of notifications) {
        commit('markReadNotification', { id: notificationId });
      }
      await ApiRequest({
        url: '/notifications',
        method: 'put',
        data: {
          notifications,
        },
      });
    },
    async onPay(_, organizationId) {
      const { data } = await ApiRequest({
        method: 'get',
        url: `${import.meta.env.VITE_API_URL}/payments/stripe/session/checkout`,
        params: {
          organizationId,
        },
      });
      window.open(data.url, '_blank');
    },
    async getStripePortalUrl({ getters }) {
      const { data } = await ApiRequest({
        method: 'get',
        url: `${import.meta.env.VITE_API_URL}/payments/stripe/portal`,
        params: {
          organizationId: getters.currentOrganization?.id,
        },
      });
      return data.url;
    },
    async getBillingDetails({ state, getters, commit }) {
      await state.organizationsPromise;
      if (getters.currentOrganization.payment) {
        const { data } = await ApiRequest({
          method: 'get',
          url: `${import.meta.env.VITE_API_URL}/payments/billing`,
          params: {
            organizationId: getters.currentOrganization.id,
          },
        });
        commit(
          'setSubscriptionEnds',
          (data.subscription.ended_at || data.subscription.current_period_end) * 1000
        );
        return data;
      }
    },
    async requestBot({ state }, { name, meetingUrl }) {
      const response = await ApiRequest({
        method: 'post',
        url: `${import.meta.env.VITE_API_URL}/bot/new`,
        data: { meetingUrl, name, organizationId: state.currentOrganizationId },
      });
      return response.data;
    },
    async recordAudio({ commit, state }, { name } = {}) {
      const canRecord = await VoiceRecorderWrapper.requestRecording();
      if (!canRecord) {
        return;
      }
      const recorder = new VoiceRecorderWrapper('RECORD_WIDGET', {
        notifications: true,
      });
      await recorder.startRecording({ name, organizationId: state.currentOrganizationId });
      commit('setRecordingWidget', true);
    },
  },
  mutations: {
    setQuery(state, val) {
      state.query = val;
    },
    setProgress(state, val) {
      state.progress = val;
    },
    setRecordingWidget(state, val) {
      state.recordingWidget = val;
    },
    setRecordStatus(state, val) {
      state.recordStatus = val;
    },
    appLoaded(state, val) {
      state.appLoaded = val;
    },
    setCurrentUser(state, val) {
      state.currentUser = val;
    },
    setUserProfile(state, val) {
      state.userProfile = val;
    },
    setGoogleIntegrationUser(state, val) {
      state.googleIntegrationUser = val;
    },
    replaceOrganization(state, val) {
      const existingOrganizationIndex = state.organizations.findIndex(({ id }) => id === val.id);
      state.organizations[existingOrganizationIndex] = val;
    },
    setOrganizations(state, val) {
      state.organizations = val;
    },
    addOrganization(state, val) {
      const existingOrganizationIndex = state.organizations.findIndex(({ id }) => id === val.id);
      if (existingOrganizationIndex === -1) {
        state.organizations.push(val);
      }
    },
    setOrganizationsPromise(state, val) {
      state.organizationsPromise = val;
    },
    resetState(state) {
      Object.assign(state, getInitialState());
    },
    addNotification(state, val) {
      const notificationId = state.notifications.findIndex(
        (notification) => notification.id === val.id
      );
      if (notificationId === -1) {
        state.notifications.push(val);
        state.notifications.sort((a, b) => {
          return b.createdAt.toMillis() - a.createdAt.toMillis();
        });
      } else {
        state.notifications[notificationId] = val;
      }
    },
    removeNotification(state, { id }) {
      state.notifications = state.notifications.filter((notification) => notification.id !== id);
    },
    markReadNotification(state, { id }) {
      const notificationId = state.notifications.findIndex(
        (notification) => notification.id === id
      );
      if (notificationId !== -1) {
        state.notifications[notificationId].isRead = true;
      }
    },
    setMiniDrawer(state, val) {
      state.miniDrawer = val;
      localStorage.setItem('miniDrawer', val);
    },
    setSubscriptionEnds(state, val) {
      state.subscriptionEnds = val;
    },
    setCurrentOrganizationId(state, val) {
      localStorage.setItem('currentOrganizationId', val);
      state.currentOrganizationId = val;
    },
  },
  getters,
});

export default store;

function getInitialState() {
  return {
    query: {},
    appLoaded: false,
    miniDrawer: localStorage.getItem('miniDrawer') === 'true',
    recordingWidget: false,
    recordStatus: {
      status: 'NONE', // "RECORDING" | "PAUSED"
      caller: null, // "RecordWidget", "SpeechSampleRecordDialog", "CommentInput"
    },
    currentUser: null,
    googleIntegrationUser: null,
    userProfile: {},
    recordings: [],
    organizations: [],
    currentOrganizationId: localStorage.getItem('currentOrganizationId') || null,
    organizationsPromise: null,
    notifications: [],
    localRecordings: [],
    subscriptionEnds: undefined,
  };
}
